import ROSLIB from 'roslib'
import axios from 'axios'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import Helpers from '@/mixins/helpers'

const MS_IN_SECONDS = 1000
const REST_UPDATE_INTERVAL_SECS = 30
const ROS_RECONNECT_INTERVAL_MS = 10 * MS_IN_SECONDS
const CAMPAIGN_TYPE_INPROGRESS = 1
// const CAMPAIGN_TYPE_UPCOMING = 2
const CAMPAIGN_TYPE_COMPLETED = 3

export default {
  name: 'BelugaClient',
  mixins: {
    Helpers
  },
  data () {
    return {
      backend_address: (location.host === 'localhost:8080') ? 'beluga-dev' : location.hostname,
      live_mode: process.env.VUE_APP_LIVEMODE === 'true', // use this flag to switch between pull (false) and rosbridge (true) version
      rosbridge_client: {
        protocol: 'ws://',
        port: '9090',
        connected: false,
        ros: null,
        keepAliveTopic: null,
        messageStateTopic: null,
        messagesTopic: null,
        responsesTopic: null,
        enableUsblPositioningService: null,
        enableUsblPingingService: null,
        disableUsblPingingAndPositioningService: null,
        startPositionCalibrationService: null,
        stopPositionCalibrationService: null,
        applyPositionEstimateService: null
      },
      rest_client: {
        protocol: location.protocol + '//', // beluga-dev, beluga-prod: http, beluga-www: https
        port: (location.host === 'localhost:8080') ? 80 : location.port,
        last_update: 0,
        interval: REST_UPDATE_INTERVAL_SECS,
        compress: true,
        update_task: null
      }
    }
  },
  computed: {
    ...mapState(['campaigns', 'platforms', 'selectedCampaign', 'selectedPlatform', 'clientId']),
    ...mapGetters(['campaignType', 'campaignSelected', 'campaignPlatforms', 'campaignTimeRange']),
    restapi_address: function () {
      return this.rest_client.protocol + this.backend_address + ':' + this.rest_client.port + '/beluga-web/api/index.php'
    },
    rosbridge_address: function () {
      return this.rosbridge_client.protocol + this.backend_address + ':' + this.rosbridge_client.port
    }
  },
  mounted () {
    // Subscribe to store mutations
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'select_campaign') {
        // Make sure that all platforms are inflated
        this.campaignSelected.platforms.forEach(platformId => {
          if (!(platformId in this.platforms)) {
            this.download_platform(platformId).then(r => {})
          } else {
            const range = this.campaignTimeRange(this.selectedCampaign)
            const platform = this.platforms[platformId]
            let start = 0
            let end = 0
            if (this.campaignType(this.selectedCampaign) === CAMPAIGN_TYPE_COMPLETED) {
              if ('last_change' in platform) {
                if (Number(platform.last_change) < range.end) {
                  start = Number(platform.last_change)
                  end = range.end
                }
              } else {
                start = range.start
                end = range.end
              }
            } else if (this.campaignType(this.selectedCampaign) === CAMPAIGN_TYPE_INPROGRESS) {
              if ('last_change' in platform) {
                start = Number(platform.last_change)
              } else {
                start = range.start
              }
            }
            // Only download data if range was properly set
            if (start > 0) {
              this.download_data(platformId, 0, 0, start, end).then(r => {})
            }
          }
        })
        // Select platform of campaign if only one platform exists, otherwise deselect all
        if (this.campaignSelected.platforms.length >= 1) {
          let platformId = 0
          if (Number(this.campaignSelected.project_platform) > 0) {
            platformId = Number(this.campaignSelected.project_platform)
            console.log('client: selecting base platform of selected campaign: ' + platformId)
          } else {
            platformId = Number(this.campaignSelected.platforms[0])
            console.log('client: selecting first platform of selected campaign: ' + platformId)
          }
          this.select_platform(platformId)
        }
        // Trigger additional update of all platforms (waiting for update task can take too long depending on interval)
        // FIXME: this.update_data()
        // Deselect media feed items if feed exists
        this.select_feed_item(null)
        // Download media feed for selected campaign if not yet processed
        this.download_media_feed()
      } else if (mutation.type === 'add_platform') {
        // Make sure that platforms sent via ros topic are fully inflated
        if ((mutation.payload.id in this.platforms) &&
          Object.keys(this.platforms[mutation.payload.id].entities).length === 0) {
          this.download_platform(mutation.payload.id).then(r => {})
        }
      } else if (mutation.type === 'select_platform') {
        // Reset selected parameter
        // this.select_parameter(null)
      }
    })

    // Initialize client and inflate data model
    this.initialize()

    // Select data update mode depending on frontend version: Ship or internet
    if (this.live_mode) {
      // Ship: Live mode enabled, connect to ROSBridge
      this.connect_ros()
      console.log('client: running in live mode (ship) with rest api and rosbridge')
    } else {
      // Internet: Start timer to pull new data periodically
      this.rest_client.update_task = setInterval(this.update_data, this.rest_client.interval * MS_IN_SECONDS)
      console.log('client: running in pull mode (internet) with rest api')
    }

    // DEBUG: Register global events
    this.$root.$on('sendMessage', (message) => {
      if (this.connected) {
        // TODO: Check message integrity
        this.messagesTopic.publish(message)
      }
    })
    this.$root.$on('enableUsblPositioning', (platformId) => {
      if (this.connected) {
        const serviceRequest = new ROSLIB.ServiceRequest({
          platform_id: platformId
        })
        this.enableUsblPositioningService.callService(serviceRequest, function (result) {
          let message = ''
          if (result.success) {
            message = 'ros client: usbl positioning enabled for platform ' + platformId
            console.log(message)
          } else {
            message = 'ros client: enabling usbl positioning for platform ' + platformId + ' failed'
            console.error(message)
          }
          alert(message)
        })
      }
    })
    this.$root.$on('enableUsblPinging', (platformId) => {
      if (this.connected) {
        const serviceRequest = new ROSLIB.ServiceRequest({
          platform_id: platformId
        })
        this.enableUsblPingingService.callService(serviceRequest, function (result) {
          let message = ''
          if (result.success) {
            message = 'ros client: usbl pinging enabled for platform ' + platformId
            console.log(message)
          } else {
            message = 'ros client: enabling usbl pinging for platform ' + platformId + ' failed'
            console.error(message)
          }
          alert(message)
        })
      }
    })
    this.$root.$on('disableUsblPingingAndPositioning', (platformId) => {
      if (this.connected) {
        const serviceRequest = new ROSLIB.ServiceRequest({
          platform_id: platformId
        })
        this.disableUsblPingingAndPositioningService.callService(serviceRequest, function (result) {
          let message = ''
          if (result.success) {
            message = 'ros client: usbl pinging/positioning disabled for platform ' + platformId
            console.log(message)
          } else {
            message = 'ros client: disabling usbl pinging/positioning for platform ' + platformId + ' failed'
            console.error(message)
          }
          alert(message)
        })
      }
    })
    this.$root.$on('startPositionCalibration', (platformId) => {
      if (this.connected) {
        const serviceRequest = new ROSLIB.ServiceRequest({
          target_id: platformId,
          method: 'average'
        })
        this.startPositionCalibrationService.callService(serviceRequest, function (result) {
          let message = ''
          if (result.success) {
            message = 'ros client: start position calibration for platform ' + platformId
            console.log(message)
          } else {
            message = 'ros client: starting position calibration for platform ' + platformId + ' failed'
            console.error(message)
          }
        })
      }
    })
    this.$root.$on('stopPositionCalibration', (platformId) => {
      if (this.connected) {
        const serviceRequest = new ROSLIB.ServiceRequest({
          target_id: platformId
        })
        this.stopPositionCalibrationService.callService(serviceRequest, function (result) {
          let message = ''
          if (result.success) {
            message = 'ros client: stop position calibration for platform ' + platformId
            console.log(message)
          } else {
            message = 'ros client: stop position calibration for platform ' + platformId + ' failed'
            console.error(message)
          }
        })
      }
    })
  },
  beforeDestroy () {
    // If live mode is enabled: Disconnect ROSBridge
    if (this.live_mode) {
      this.disconnect_ros()
    }
    // Unsubscribe from mutations
    this.unsubscribe()
    // Clear update task if available
    if (this.rest_client.update_task !== null) {
      clearInterval(this.rest_client.update_task)
    }
  },
  methods: {
    ...mapActions(['handle_campaign', 'handle_platform', 'handle_parameter', 'handle_entity', 'handle_update',
      'handle_channel_info', 'handle_channel_state', 'handle_response', 'handle_keep_alive']),
    ...mapMutations(['select_platform', 'select_parameter', 'select_feed_item']),
    optimizeTimestamp: function (timestamp) {
      // Restrict timestamps to 0 or 30 seconds per minute to allow query caching on server side
      if (timestamp > 0) {
        const timestamp0 = Math.floor(timestamp / 60) * 60
        const timestamp30 = timestamp0 + 30
        if (timestamp - timestamp0 > 30) {
          return timestamp30
        }
        return timestamp0
      }
      return timestamp
    },
    initialize: function () {
      console.log('client: initializing, downloading campaigns..')
      // Download campaigns
      axios.get(this.restapi_address + '?cmd=campaigns&session=' + this.clientId)
        .then(response => {
          // Send campaigns to data model
          response.data.campaigns.forEach(campaign => {
            this.$store.dispatch('handle_campaign', campaign).then(r => {})
          })
        })
        .catch(err => {
          console.error('beluga client: downloading initial data from backend failed. ' + err)
        })
    },
    update_data: function () {
      // Check for inactivity
      if (this.$store.state.idleVue.isIdle) {
        console.warn('beluga client: data update skipped due to user inactivity')
        return
      }
      // Update all platforms of selected campaign if in progress
      if (this.campaignType(this.selectedCampaign) === CAMPAIGN_TYPE_INPROGRESS) {
        this.campaignPlatforms(this.selectedCampaign).forEach(platform => {
          console.debug('beluga client: checking for new data of platform ' + platform.id)
          this.download_data(platform.id, 0, 0, platform.last_update, 0).then(r => {})
        })
      }
    },
    download_platform: function (platformId) {
      console.debug('beluga client: downloading structure of platform ' + platformId)
      const payload = {
        platform_id: Number(platformId)
      }
      return axios.post(this.restapi_address + '?cmd=platform&session=' + this.clientId, payload)
        .then(response => {
          if ('platform' in response.data) {
            this.$store.dispatch('handle_platform', response.data.platform)
              .then(r => {
                // Download initial past and current data of platform
                const range = this.campaignTimeRange(this.selectedCampaign)
                if (range.start > 0) {
                  this.download_data(platformId, 0, 0, range.start, range.end).then(r => {})
                }
                // Download sections (missions, legs, deployments, voyages..)
                this.download_sections(platformId, this.selectedCampaign).then(r => {})
              })
          } else {
            console.warn('beluga client: downloading platform ' + platformId + ' failed, does not exist')
          }
        })
        .catch(err => {
          console.error('beluga client: downloading platform from backend failed. ' + err)
        })
    },
    download_data: function (platformId, entityId, parameterId, start, end) {
      const payload = {
        platform_id: platformId,
        entity_id: entityId,
        parameter_id: parameterId,
        timestamp_start: this.optimizeTimestamp(start),
        timestamp_end: this.optimizeTimestamp(end),
        compress: this.rest_client.compress
      }
      const endText = (Number(payload.timestamp_end) === 0) ? 'now' : payload.timestamp_end
      console.debug('beluga client: downloading data of platform ' + platformId + ' from ' + payload.timestamp_start + ' to ' + endText)
      return axios.post(this.restapi_address + '?cmd=data&session=' + this.clientId, payload)
        .then(response => {
          if ('values' in response.data) {
            // Save update timestamp to response struct
            // response.data.timestamp = Math.floor((new Date()).getTime() / 1000)
            response.data.timestamp = Math.floor((new Date()).getTime() / 1000)
            // Send parameter updates to data model
            this.$store.dispatch('handle_update', response.data).then(r => {})
          } else {
            // Initialize last change to enable ROS Bridge in live mode
            if (response.data.platform_id in this.platforms) {
              this.platforms[response.data.platform_id].last_change = 0
            }
          }
        })
        .catch(err => {
          console.error('beluga client: downloading data from beluga backend failed. ' + err)
        })
    },
    download_sections: function (platformId, campaignId) {
      console.debug('beluga client: downloading sections of platform ' + platformId + ' in campaign ' + campaignId)
      const payload = {
        platform_id: platformId,
        campaign_id: campaignId
      }
      return axios.post(this.restapi_address + '?cmd=sections&session=' + this.clientId, payload)
        .then(response => {
          if ('sections' in response.data) {
            // Send parameter updates to data model
            this.$store.dispatch('handle_sections', response.data.sections).then(r => {})
          }
        })
        .catch(err => {
          console.error('beluga client: downloading sections from beluga backend failed. ' + err)
        })
    },
    download_media_feed: function () {
      if (!('project_feed_data' in this.campaignSelected) && this.campaignSelected.project_feed.trim() !== '') {
        console.log('beluga client: downloading media feed of campaign "' + this.campaignSelected.name + '"..')
        axios.get(this.campaignSelected.project_feed)
          .then(response => {
            const payload = {}
            payload.campaignId = this.selectedCampaign
            payload.mediaFeed = response.data
            this.$store.dispatch('handle_media_feed', payload).then(r => {})
          })
          .catch(err => {
            console.error('beluga client: downloading media feed for selected campaign failed. ' + err)
          })
      }
    },
    connect_ros: function () {
      const vm = this
      // Initialize ROSBridge client
      this.ros = new ROSLIB.Ros({
        url: vm.rosbridge_address
      })
      this.ros.on('connection', this.init_ros)
      this.ros.on('error', (error) => {
        this.connected = false
        console.error('beluga client: connecting to beluga backend failed: ' + error)
      })
      this.ros.on('close', () => {
        this.connected = false
        console.error('beluga client: connection to beluga backend was closed, reconnecting in ' + ROS_RECONNECT_INTERVAL_MS / MS_IN_SECONDS + ' seconds..')
        setTimeout(this.connect_ros, ROS_RECONNECT_INTERVAL_MS)
      })
    },
    init_ros: function () {
      // Create listeners to data model topics
      const platformListener = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/model/platforms',
        messageType: 'beluga_lib/BelugaDataModelPlatform'
      })
      platformListener.subscribe(this.handle_platform)
      const updateListener = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/model/updates',
        messageType: 'beluga_lib/BelugaDataModelUpdate'
      })
      updateListener.subscribe(this.handle_update)
      // Create listener to channel state topic
      const channelInfoListener = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/comms/channel_info',
        messageType: 'beluga_lib/BelugaChannelInfo'
      })
      channelInfoListener.subscribe(this.handle_channel_info)
      // Create listener to channel state topic
      const channelStateListener = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/comms/channel_state',
        messageType: 'beluga_lib/BelugaChannelState'
      })
      channelStateListener.subscribe(this.handle_channel_state)
      const responsesListener = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/model/input',
        messageType: 'beluga_lib/BelugaDataModelInput'
      })
      responsesListener.subscribe(this.handle_response)
      // Keep alive
      this.keepAliveTopic = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/comms/keep_alive',
        messageType: 'beluga_lib/BelugaKeepAlive'
      })
      this.keepAliveTopic.subscribe(this.handle_keep_alive)
      // Message state
      this.messageStateTopic = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/comms/message_state',
        messageType: 'beluga_lib/BelugaMessageState'
      })
      // this.messageStateTopic.subscribe(this.handle_message_state)
      // Messages topic
      this.messagesTopic = new ROSLIB.Topic({
        ros: this.ros,
        name: '/backend/comms/messages',
        messageType: 'beluga_lib/BelugaMessageContainer'
      })
      this.messagesTopic.subscribe(this.handle_messages)

      this.enableUsblPositioningService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/nav/enable_usbl_positioning',
        serviceType: 'beluga_lib/UsblPositioningPlatformIDs'
      })
      this.enableUsblPingingService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/nav/enable_usbl_pinging',
        serviceType: 'beluga_lib/UsblPositioningPlatformIDs'
      })
      this.disableUsblPingingAndPositioningService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/nav/disable_usbl_pinging_and_positioning',
        serviceType: 'beluga_lib/UsblPositioningPlatformIDs'
      })
      this.startPositionCalibrationService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/nav/start_position_calibration',
        serviceType: 'beluga_lib/BelugaStartPositionCalib'
      })

      this.stopPositionCalibrationService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/nav/stop_position_calibration',
        serviceType: 'beluga_lib/BelugaStopPositionCalib'
      })

      // Republish channel data service
      const republishChannelsService = new ROSLIB.Service({
        ros: this.ros,
        name: '/backend/comms/republish_channels',
        serviceType: 'std_srvs/TriggerRequest'
      })
      const requestChannels = new ROSLIB.ServiceRequest({})
      republishChannelsService.callService(requestChannels, function (result) {
        // console.log('ros client: channels successfully republished')
      })

      // Set status to connected
      console.debug('beluga client: rosbridge connected, successfully subscribed to topics')
      this.connected = true
    },
    disconnect_ros: function () {
      this.ros.close()
    },
    handle_messages: function (message) {
    }
  }
}
